import { useEffect, useMemo, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  ChatBubbleOvalLeftIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  LockClosedIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { Message } from '@flock-types/chat.types';
import { getRecentEmojis } from '@/lib/recentEmojis';
import ReactionPicker from './ReactionPicker';

interface Props {
  message: Message;
  own: boolean;
  isDM: boolean;
  isLeader: boolean;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onPrivateReply: () => void;
  onMessageUser: () => void;
  onEdit: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function ActionRow({
  icon,
  label,
  danger,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex min-h-touch w-full items-center gap-3 border-t border-flock-navy-100 px-4 py-3 text-left text-base font-medium transition',
        danger ? 'text-red-600 hover:bg-red-50' : 'text-flock-navy-900 hover:bg-flock-navy-50'
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Message actions modal — web port of the flock-expo context menu
 * (MessageBubble's long-press modal): recent-emoji quick row with a "+"
 * expander, then Reply / Privately Reply / Message {name} / Copy text /
 * Edit / Pin / Delete, role- and ownership-gated like mobile.
 */
export default function MessageContextMenu({
  message,
  own,
  isDM,
  isLeader,
  onReact,
  onReply,
  onPrivateReply,
  onMessageUser,
  onEdit,
  onTogglePin,
  onDelete,
  onClose,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const recentEmojis = useMemo(() => getRecentEmojis(), []);
  const firstName = message.userName?.split(' ')[0] || 'them';
  const iconClass = 'h-5 w-5 shrink-0 text-flock-navy-500';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = async () => {
    if (!message.content?.text) return;
    try {
      await navigator.clipboard.writeText(message.content.text);
      setCopied(true);
      setTimeout(onClose, 700);
    } catch {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-xs overflow-hidden rounded-2xl bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {pickerOpen ? (
          <ReactionPicker
            inline
            onSelect={(emoji) => onReact(emoji)}
            onClose={() => setPickerOpen(false)}
          />
        ) : (
          <>
            {/* Quick-react row: the user's recent emojis + "+" for the full picker */}
            <div className="flex flex-wrap justify-center gap-2 p-3">
              {recentEmojis.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(emoji)}
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-flock-navy-50 text-2xl transition hover:bg-flock-navy-100"
                >
                  {emoji}
                </button>
              ))}
              <button
                onClick={() => setPickerOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-flock-navy-100 text-lg font-semibold text-flock-navy-500 transition hover:bg-flock-navy-200"
                title="More reactions"
              >
                +
              </button>
            </div>

            <ActionRow
              icon={<ArrowUturnLeftIcon className={iconClass} />}
              label="Reply"
              onClick={onReply}
            />

            {!own && !isDM && (
              <ActionRow
                icon={<LockClosedIcon className={iconClass} />}
                label={`Privately Reply to ${firstName}`}
                onClick={onPrivateReply}
              />
            )}

            {!own && !isDM && (
              <ActionRow
                icon={<ChatBubbleOvalLeftIcon className={iconClass} />}
                label={`Message ${firstName}`}
                onClick={onMessageUser}
              />
            )}

            {message.content?.text && (
              <ActionRow
                icon={
                  copied ? (
                    <CheckIcon className="h-5 w-5 shrink-0 text-green-600" />
                  ) : (
                    <DocumentDuplicateIcon className={iconClass} />
                  )
                }
                label={copied ? 'Copied!' : 'Copy text'}
                onClick={handleCopy}
              />
            )}

            {own && message.type === 'text' && (
              <ActionRow
                icon={<PencilIcon className={iconClass} />}
                label="Edit"
                onClick={onEdit}
              />
            )}

            {isLeader && (
              <ActionRow
                icon={<MapPinIcon className={iconClass} />}
                label={message.isPinned ? 'Unpin' : 'Pin'}
                onClick={onTogglePin}
              />
            )}

            {(own || isLeader) && (
              <ActionRow
                icon={<TrashIcon className="h-5 w-5 shrink-0 text-red-600" />}
                label="Delete"
                danger
                onClick={onDelete}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
